import { toast } from 'sonner';
import i18next from 'i18next';
import { createIntegrationLogger } from '@/lib/app-log.js';
import { buildGroupedOwnerSelectOptionsFromEntities } from '@/lib/grouped-owner-select-options.js';
import { useAssetCategoryStore } from '@/store/asset-category.store';
import { useTenantStore } from '@/store/tenant.store';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

const integrationLog = createIntegrationLogger('asset-category');

export class AssetCategoryIntegration {
    constructor(service, assetGroupService, userService) {
        this.service = service;
        this.assetGroupService = assetGroupService;
        this.userService = userService;
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    /** Lookup list without mutating the category admin store or toasting. */
    async queryAll(params = {}) {
        return this.service.getAll(params);
    }

    async fetchAssetCategories(params = {}) {
        const store = useAssetCategoryStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!tenantId) return;

        store.setLoading(true);
        store.setError(null);

        try {
            const currentPagination = store.pagination;

            // Merge params with store state to persist filters/search/sort when only one changes
            const search = params.search !== undefined ? params.search : store.search;
            const sortBy = params.sortBy || store.sorting.sortBy;
            const sortOrder = params.sortDirection || store.sorting.sortDirection;
            const filters = params.columnFilters || store.columnFilters || {};

            // Map filters to backend format (prefix with 'filter_')
            const mappedFilters = {};
            Object.keys(filters).forEach(key => {
                mappedFilters[`filter_${key}`] = filters[key];
            });

            const queryParams = {
                page: params.page || currentPagination.currentPage,
                pageSize: params.pageSize || currentPagination.itemsPerPage,
                search,
                sortBy,
                sortOrder,
                ...mappedFilters, // Add prefixed dynamic filters
                tenantId
            };

            const response = await this.service.getAll(queryParams);

            store.setData(response.data);

            const meta = response.meta || {};
            store.setPagination({
                currentPage: parseInt(meta.page) || 1,
                itemsPerPage: parseInt(meta.pageSize) || 10,
                totalItems: parseInt(meta.total) || 0,
                totalPages: parseInt(meta.totalPages) || 1,
                hasNextPage: (parseInt(meta.page) || 1) < (parseInt(meta.totalPages) || 1),
                hasPreviousPage: (parseInt(meta.page) || 1) > 1
            });

            if (response.uniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async getAssetCategory(id) {
        const store = useAssetCategoryStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setSelectedItem(response.data);
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async createAssetCategory(data) {
        const store = useAssetCategoryStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        try {
            const response = await this.service.create({
                ...data,
                // Ensure assetGroupId is passed
                assetGroupId: data.assetGroupId,
                tenantId
            });

            toast.success(i18next.t('assetInventoryAssetCategory:messages.createSuccess'));

            // Refresh list
            await this.fetchAssetCategories();
            store.closeForm();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async updateAssetCategory(id, data) {
        const store = useAssetCategoryStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('assetInventoryAssetCategory:messages.updateSuccess'));

            // Refresh list
            await this.fetchAssetCategories();
            store.closeForm();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteAssetCategory(id) {
        const store = useAssetCategoryStore.getState();
        store.setLoading(true);

        try {
            await this.service.delete(id);

            toast.success(i18next.t('assetInventoryAssetCategory:messages.deleteSuccess'));

            // Update local state immediately for better UX or refresh
            await this.fetchAssetCategories();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDeleteAssetCategories(ids) {
        const store = useAssetCategoryStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('assetInventoryAssetCategory:messages.bulkDeleteSuccess', { count: response.deletedCount }));

            await this.fetchAssetCategories();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchDropdownData() {
        const store = useAssetCategoryStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        try {
            const { useReferenceStore } = await import('@/store/reference.store.js');
            const { hydrationManager } = await import('@/lib/services/reference-hydration.js');
            const { fetchDropdownOptionsFromReference } = await import('@/lib/reference-dropdown.helper.js');

            let refState = useReferenceStore.getState();
            if (tenantId && !refState.lastHydratedAt) {
                await hydrationManager.hydrateAll();
                refState = useReferenceStore.getState();
            }

            // assetGroup is not a core reference entity. hydrateAll() is skipped once
            // lastHydratedAt is set, so a page-level ensure is required (same pattern as
            // assets.integration fetchFilterOptionData).
            const requiredEntities = ['assetGroup', 'user', 'team', 'role'];
            const missingEntities = requiredEntities.filter(
                (entity) => tenantId && !refState.hydratedEntities?.[entity],
            );
            if (missingEntities.length > 0) {
                await Promise.all(missingEntities.map((entity) => hydrationManager.ensureEntity(entity)));
                refState = useReferenceStore.getState();
            }

            const emptyEntities = requiredEntities.filter(
                (entity) => tenantId && (refState.entities?.[entity] || []).length === 0,
            );
            if (emptyEntities.length > 0) {
                await hydrationManager.hydrateAll({ force: true, bulkEntities: emptyEntities });
                refState = useReferenceStore.getState();
            }

            const entities = refState.entities || {};
            const assetGroupsResult = await fetchDropdownOptionsFromReference('assetGroup', async () => {
                if (!this.assetGroupService) {
                    return { success: true, data: [] };
                }
                try {
                    const response = await this.assetGroupService.getAll({ tenantId, pageSize: 1000 });
                    return {
                        success: true,
                        data: (response.data || []).map((group) => ({
                            ...group,
                            value: group._id || group.id,
                            label: group.name,
                        })),
                    };
                } catch (fallbackError) {
                    integrationLog.error('Asset group dropdown fallback failed', { error: fallbackError.message });
                    return { success: true, data: [] };
                }
            });
            const categoriesResponse = await this.service.getAll({ tenantId, pageSize: 100 });

            const dropdownData = {
                assetGroups: assetGroupsResult?.data || entities.assetGroup || [],
                owners: buildGroupedOwnerSelectOptionsFromEntities(entities),
                categories: categoriesResponse.data || [],
                securityValues: [
                    { value: 1, name: '1 - Çok Düşük' },
                    { value: 2, name: '2 - Düşük' },
                    { value: 3, name: '3 - Orta' },
                    { value: 4, name: '4 - Yüksek' },
                    { value: 5, name: '5 - Çok Yüksek' },
                ],
            };

            store.setDropdownData(dropdownData);
            return { success: true, data: dropdownData };
        } catch (error) {
            integrationLog.error('Failed to fetch dropdown data', { error: error.message });
            return { success: false, error: error.message };
        }
    }
}
